import { ErrorBoundary } from '@sentry/react';
import { Stacked, Tabs } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadablePlayerSelectedGamesForQueryAtom } from '@/atoms/filters/player/playerGames';
import { playerRadarTemplateConfigsAtom } from '@/atoms/queries/userConfigs';
import { PLAYER_PREVIEW_TAB } from '@/consts/searchKeys';
import { useQueryParam } from '@/hooks/useQueryParam';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { StatusTranslationKey } from '@/types/status';
import { ContentState } from '../../contentState/ContentState';
import { GeneralFallback } from '../../contentState/GeneralFallback';
import { PlayerRadarChart } from '../../player/radar/PlayerRadarChart';
import RadarTemplateDropdown from '../../radar/RadarTemplateDropdown';
import SummaryProfileDropdown from '../../radar/SummaryProfileDropdown';
import { PercentileDataPillLegend } from '../../radar/table/PercentileDataPillLegend';
import { PlayerRadarTable } from '../../radar/table/PlayerRadarTable';
import { PlayerPreviewSummary } from './PlayerPreviewSummary';

const validTabValues = ['radar', 'summary'] as const;
type PlayerPreviewTabValue = (typeof validTabValues)[number];

const PlayerPreviewRadarContent = () => {
  const { t } = useTranslation(['radar', 'entity']);
  const label = t('dropdownLabel', { entity: t('player_one', { ns: 'entity' }) });
  const loadableSelectedGames = useAtomValue(loadablePlayerSelectedGamesForQueryAtom);
  const hasNoGames = loadableSelectedGames.state === 'hasData' && loadableSelectedGames.data.length === 0;

  if (hasNoGames) {
    return <GeneralFallback type="noData" />;
  }

  return (
    <div className="flex flex-col relative p-4 gap-4">
      <div className="w-56">
        <RadarTemplateDropdown radarTemplateConfigsAtom={playerRadarTemplateConfigsAtom} label={label} />
      </div>
      <div className="lg:max-w-[600px] w-available mx-auto [&>article_div]:p-0">
        <PlayerRadarChart id="player-preview-radar" isInteractive={false} />
      </div>
      <Stacked direction="column" justifyContent="space-between">
        <div className="overflow-x-auto border border-canvas-primary-main [&>table]:border-0">
          <PlayerRadarTable isInteractive={false} />
        </div>
        <PercentileDataPillLegend />
      </Stacked>
    </div>
  );
};

const PlayerPreviewSummaryContent = () => {
  const { t } = useTranslation('radar');

  return (
    <div className="flex flex-col relative p-4 gap-4 h-full">
      <div className="w-56 shrink-0">
        <SummaryProfileDropdown
          radarTemplateConfigsAtom={playerRadarTemplateConfigsAtom}
          label={t('summaryProfileDropdownLabel')}
        />
      </div>
      <ErrorBoundary fallback={<ContentState status={StatusTranslationKey.ERROR} />}>
        <PlayerPreviewSummary />
      </ErrorBoundary>
    </div>
  );
};

export const PlayerPreviewBody = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = useQueryParam(PLAYER_PREVIEW_TAB);
  const updateQueryString = useUpdateQueryString(searchParams);

  // guard against invalid tab values in the url
  const activeTab: PlayerPreviewTabValue = validTabValues.find(tab => tab === tabParam) ?? 'radar';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Tabs
        activeTab={activeTab}
        onTabChange={(_: MouseEvent, { value }: { value: string }) =>
          navigate({ search: updateQueryString([{ key: PLAYER_PREVIEW_TAB, value }]) })
        }
        tabs={[
          {
            label: t('secondary.radar'),
            value: 'radar',
          },
          {
            displayText: 'right',
            label: t('secondary.summary'),
            icon: 'AiSummary',
            value: 'summary',
          },
        ]}
      />
      <div className="flex-1 min-h-0 overflow-y-auto">
        {activeTab === 'radar' && <PlayerPreviewRadarContent />}
        {activeTab === 'summary' && <PlayerPreviewSummaryContent />}
      </div>
    </div>
  );
};
